import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  Decision, DomicilioFiscal, EtapaRuta, fiscalDe, fiscalSinVacios, PIEZAS_FISCAL, EventoSolicitud, MotivoRechazo, PAPELES, PapelContacto, Solicitud,
  SolicitudesAltaService, TipoEvento, papelesDe, textoFaltanContactos, textoTecnicoObligatorio, CentroBeneficio, AccesoAlta,
} from 'src/app/services/workflows/solicitudes-alta.service';
import { mensajeDeError } from 'src/app/services/workflows/workflows.service';
import { PasoRiel } from '../riel-pasos/riel-pasos.component';
import { PuestaEnMarcha } from '../puesta-en-marcha/puesta-en-marcha.component';
import { OpcionMenu } from '../kb-menu/kb-menu.component';
import { plazoDe, nombreDelPlazo } from '../../comercios/plazo-unidad';

/** Los meses cortos en castellano, para la fecha corta y la fecha con hora del historial. */
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «08-oct» — sin hora, para «la tienes tú desde…». */
function fechaCorta(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}-${MESES[d.getMonth()]}`;
}

/** «08-oct 09:12» — con hora, para cada renglón del historial. */
function fechaHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${fechaCorta(iso)} ${hh}:${mm}`;
}

/** Etiquetas de los campos de `datos`, para el evento DATOS del historial («cambió…»). */
const ETIQUETAS_CAMPO: { [campo: string]: string } = {
  nombre_comercial: 'el nombre comercial', razon_social: 'la razón social', rif: 'el RIF',
  centros_beneficio: 'los centros de beneficio', cuenta: 'la cuenta bancaria', tienda: 'la tienda y su caja',
  direccion_fiscal: 'la dirección fiscal', fiscal: 'el domicilio fiscal', descripcion: 'la descripción', contactos: 'los contactos',
  tipo_persona: 'el tipo de persona', telefono: 'el teléfono', correo: 'el correo',
};

/** Las tres pestañas de la solicitud; el estado vive en `?pestana=`. */
export type Pestana = 'expediente' | 'historial' | 'decision';
const PESTANAS: Pestana[] = ['expediente', 'historial', 'decision'];

/** Las secciones del expediente, como las nombra el núcleo en `secciones_editables`. */
type Seccion = 'DATOS' | 'CANALES' | 'CONTACTOS' | 'DOCUMENTOS' | 'CONDICIONES' | 'CUENTA';

/** El paso de «Completar mi parte» (`/cargar?paso=`) que arregla cada sección (contrato: B y C). */
const PASO_DE_SECCION: { [s in Seccion]: string } = {
  DATOS: 'datos', CANALES: 'canales', CONTACTOS: 'contactos', DOCUMENTOS: 'documentos', CONDICIONES: 'plazo', CUENTA: 'cuenta',
};

/** Quién llena cada sección (`tipos-solicitud.js`, `secciones[].la_llena`). */
const QUIEN_LLENA: { [s in Seccion]: string } = {
  DATOS: 'Ejecutivo comercial', CANALES: 'Ejecutivo comercial', CONTACTOS: 'Ejecutivo comercial',
  DOCUMENTOS: 'Ejecutivo comercial', CONDICIONES: 'Gerente comercial', CUENTA: 'Ejecutivo comercial',
};

/** La franja «Lo que te toca»: una frase de estado y, como mucho, UN botón principal. */
interface Franja {
  rotulo: string;
  tarea: string | null;
  linea: string;
  /** Segundo renglón (el comentario del rechazo). */
  extra: string | null;
  tono: '' | 'exito' | 'alerta' | 'peligro';
  // VER_COMERCIO / REINTENTAR_ALTA: el efecto de una aprobada (contrato CONTRATO-ALTA-AUTOMATICA.md
  // §2) — HECHO lleva al comercio recién nacido, FALLO ofrece reintentar `ejecutarAlta`.
  principal: 'TOMAR' | 'CONTINUAR' | 'DECIDIR' | 'VER_COMERCIO' | 'REINTENTAR_ALTA' | null;
  secundaria: 'DECIDIR' | 'REVISAR' | null;
  soltar: boolean;
  /** Juan, 2026-10-08: la tarea se lee en puntos, no en un párrafo. Sólo cuando la etapa es tuya. */
  puntos: PuntoTarea[];
}

/** Un punto de la etapa con su estado («Contactos · Administrador y finanzas», hecho o no). */
interface PuntoTarea { nombre: string; detalle: string; hecho: boolean; falta: boolean; }

/** Un renglón de «Lo que falta para aprobar», con el paso que lo arregla si te toca a ti. */
interface Falta { texto: string; paso: string | null; quien: string | null; }

/**
 * «La solicitud» — ruta `/console/altas/:id` (CONTRATO-REDISENO-ALTAS.md, pantalla A). De arriba
 * abajo: la cabecera con el riel del workflow (fija bajo la barra), la franja «Lo que te toca» con un
 * solo botón principal, y tres pestañas —Expediente (solo lectura), Historial, Decisión—. Toda la
 * edición del expediente vive en «Completar mi parte» (`/console/altas/:id/cargar`).
 *
 * 🔴 Carga `obtener`, `historial` y `motivos()` en paralelo y los tres se vuelven a pedir después de
 * CADA acción (tomar, soltar, comentar, decidir): es el propio núcleo el que decide qué puede hacer el
 * que mira ahora (campo `acciones`), y eso cambia con cada acción.
 */
@Component({
  selector: 'app-alta-solicitud',
  templateUrl: './alta-solicitud.component.html',
  styleUrls: ['./alta-solicitud.component.css'],
})
export class AltaSolicitudComponent implements OnInit, OnDestroy {
  cargando = true;
  error = '';
  detalle: string[] = [];
  aviso = '';
  noExiste = false;

  sol: Solicitud | null = null;
  eventos: EventoSolicitud[] = [];
  motivosList: MotivoRechazo[] = [];
  /** Solo tras decidir (H-«volver a la bandeja»): el aviso de éxito suma un enlace de vuelta. */
  avisoConVolver = false;

  PAPELES = PAPELES;
  PIEZAS_FISCAL = PIEZAS_FISCAL;
  /** El domicilio fiscal de la solicitud en sus cinco piezas (las viejas solo traen `direccion_fiscal`: va como calle). */
  get fiscalSol(): Required<DomicilioFiscal> { return fiscalDe(this.sol?.datos); }

  // Lo que se arma una vez por carga (no en getters): un `*ngFor` sobre un arreglo nuevo en cada
  // ciclo vuelve a dibujar la lista entera.
  pasosRiel: PasoRiel[] = [];
  franja: Franja | null = null;
  historialAgrupado: Array<{ cabecera: string | null; ev: EventoSolicitud }> = [];
  faltas: Falta[] = [];

  pestana: Pestana = 'expediente';

  id = '';
  private sub?: Subscription;
  private subQuery?: Subscription;

  constructor(private _sa: SolicitudesAltaService, private route: ActivatedRoute, private router: Router,
              private _merchant: MerchantService) { }

  // ── URL propia: el paso «Dirección propia del portal» de la puesta en marcha (maqueta aprobada) ──

  /** El paso, si el núcleo lo trae (siempre lo trae; con el interruptor apagado llega NO_APLICA). */
  get pasoDireccion(): any {
    const g = this.sol?.puesta_en_marcha?.grupos || [];
    for (const x of g) { const p = (x.pasos || []).find((q: any) => q.clave === 'DIRECCION_PROPIA'); if (p) return p; }
    return null;
  }
  /** El host sale del detalle del núcleo («<host> lista» / «<host> en preparación…»). */
  get hostDireccion(): string {
    const m = /^([a-z0-9.-]+\.[a-z]+)\b/i.exec(String(this.pasoDireccion?.detalle || ''));
    return m ? m[1] : '';
  }
  probandoDireccion = false;
  probarDireccion = async (): Promise<void> => {
    const code = this.sol?.efecto?.detalle?.comercio?.code;
    if (!code || this.probandoDireccion) return;
    this.probandoDireccion = true; this.error = ''; this.detalle = [];
    try {
      await this._merchant.revisarDireccion(code);
      // La revisión pone al día la ficha; el paso de la puesta en marcha se reintenta para que también lo refleje.
      if (this.sol?.acciones.reintentar_puesta_en_marcha) await this._sa.reintentarPaso(this.id, 'DIRECCION_PROPIA');
      await this.recargar('Se revisó la dirección.');
    } catch (e: any) {
      this.fallo(e, 'No se pudo revisar la dirección.');
    } finally {
      this.probandoDireccion = false;
    }
  };

  // ── Tomar / soltar ─────────────────────────────────────────────────────────
  tomando = false;
  soltando = false;
  /** Minuta del 5-oct: «es fácil desasignarse por error». Liberar va en el «⋯» y se confirma ahí mismo. */
  confirmarSoltar = false;
  readonly opcionesFranja: OpcionMenu[] = [
    { clave: 'liberar', texto: 'Liberar la solicitud', icono: 'fa-sign-out-alt', confirmar: 'Sí, liberarla',
      tip: 'Vuelve a la bandeja y otra persona la puede tomar' },
  ];

  menuFranja(clave: string): void { if (clave === 'liberar') this.soltar(); }
  centros: CentroBeneficio[] = [];

  /** Los centros de beneficio marcados, con su nombre del catálogo («—» si no hay ninguno). */
  get centrosTexto(): string {
    const cods = this.sol?.datos.centros_beneficio || [];
    return cods.map((c) => this.centros.find((x) => x.codigo === c)?.nombre || c).join(', ');
  }

  // ── Comentar ──────────────────────────────────────────────────────────────
  comentarioTexto = '';
  comentando = false;

  // ── Decidir (aprobar / devolver / rechazar) ──────────────────────────────────
  decision: Decision | '' = '';
  comentarioDecision = '';
  motivoRechazo = '';
  // Cambio de Juan (§5): los documentos NO frenan, pero aprobar sin ellos exige esta nota.
  notaDocumentos = '';
  confirmandoRechazo = false;
  decidiendo = false;

  ngOnInit(): void {
    // La pestaña SIEMPRE sale de `?pestana=`, suscrita (no del `snapshot`, que no avisa si cambia
    // sin recrear el componente) y ANTES de `cargar()`: así, si las dos emiten en el mismo tick, la
    // pestaña ya está puesta cuando se arma la primera vista. Nada más la toca después de este punto.
    this.subQuery = this.route.queryParamMap.subscribe((q) => {
      const p = q.get('pestana') as Pestana;
      this.pestana = PESTANAS.includes(p) ? p : 'expediente';
    });
    this.sub = this.route.paramMap.subscribe((p) => {
      this.guardarComentarios();
      this.id = p.get('id') || ''; this.comentarioTexto = ''; this.comentarioDecision = ''; this.notaDocumentos = '';
      this.recuperarComentarios(); this.cargar();
    });
  }

  ngOnDestroy(): void {
    this.guardarComentarios(); this.sub?.unsubscribe(); this.subQuery?.unsubscribe();
    this.detenerSondeoPuestaEnMarcha();
  }

  // ── Pestañas ──────────────────────────────────────────────────────────────

  /** Cambia de pestaña dejándolo en la dirección (`?pestana=`), sin sumar un paso al «atrás». */
  irA(p: Pestana): void {
    this.pestana = p;
    this.router.navigate([], { relativeTo: this.route, queryParams: { pestana: p }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // Lo que se está escribiendo no se pierde al recargar o salir: se guarda en la pestaña, por solicitud.
  private get llaveComentarios(): string { return `alta-comentarios-${this.id}`; }

  @HostListener('window:beforeunload')
  guardarComentarios(): void {
    if (!this.id) return;
    try {
      if (this.comentarioTexto.trim() || this.comentarioDecision.trim() || this.notaDocumentos.trim()) {
        sessionStorage.setItem(this.llaveComentarios, JSON.stringify({
          seguimiento: this.comentarioTexto, decision: this.comentarioDecision, notaDocumentos: this.notaDocumentos,
        }));
      } else sessionStorage.removeItem(this.llaveComentarios);
    } catch (e) { /* sin almacenamiento: no se guarda */ }
  }

  private recuperarComentarios(): void {
    try {
      const b = JSON.parse(sessionStorage.getItem(this.llaveComentarios) || 'null');
      if (b) { this.comentarioTexto = b.seguimiento || ''; this.comentarioDecision = b.decision || ''; this.notaDocumentos = b.notaDocumentos || ''; }
    } catch (e) { /* nada */ }
  }

  // ── Carga ─────────────────────────────────────────────────────────────────

  /** Las tres llamadas del contrato, en paralelo. La usan tanto la carga inicial como cada acción. */
  private async traer(): Promise<void> {
    const [sol, eventos, motivosList, centros] = await Promise.all([
      this._sa.obtener(this.id), this._sa.historial(this.id), this._sa.motivos(),
      this.centros.length ? Promise.resolve(this.centros) : this._sa.centrosBeneficio().catch(() => [] as CentroBeneficio[]),
    ]);
    this.centros = centros;
    this.sol = sol;
    this.eventos = eventos;
    this.cacheResumen.clear();
    this.motivosList = motivosList;
    this.prepararDecision();
    this.armarVista();
    void this.traerAccesos();
  }

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false; this.noExiste = false;
    try {
      await this.traer();
      // El aviso que dejó la pantalla anterior («Se creó la solicitud…»), una sola vez.
      const venia = (history.state && history.state.aviso) || '';
      if (venia) { this.aviso = venia; history.replaceState({ ...history.state, aviso: null }, ''); }
    } catch (e: any) {
      this.sol = null;
      this.noExiste = e?.status === 404;
      // 404 es un hecho, no un fallo: no lleva aviso rojo (contrato).
      if (!this.noExiste) this.error = mensajeDeError(e, 'No se pudo traer la solicitud.');
    } finally {
      this.cargando = false;
    }
  };

  /** Después de cada acción: vuelve a pedir las tres y deja el aviso de qué pasó. */
  private recargar = async (avisoOk: string): Promise<void> => {
    try {
      await this.traer();
      this.aviso = avisoOk;
    } catch (e: any) {
      this.fallo(e, 'La acción se hizo, pero no se pudo actualizar la pantalla. Vuelve a consultarla.');
    }
  };

  private fallo(e: any, porOmision: string): void {
    this.error = mensajeDeError(e, porOmision);
    this.detalle = Array.isArray(e?.error?.detalle) ? e.error.detalle : [];
  }

  /**
   * H-02: un 403 o un 409 significa que otro cambió la solicitud mientras mirabas (ya la tomó otro,
   * ya no la tienes, cambió de etapa…). Sin esto, la pantalla queda ofreciendo botones que ya no
   * valen. Se vuelve a pedir la solicitud SIN tapar el error que ya se mostró.
   */
  private async refrescarSiConflicto(e: any): Promise<void> {
    if (e?.status === 403 || e?.status === 409) {
      try { await this.traer(); } catch { /* si esto también falla, queda el error ya mostrado */ }
    }
  }

  /** Lo que depende solo de `sol` y `eventos`: el riel, la franja, el historial y lo que falta. */
  private armarVista(): void {
    this.pasosRiel = this.armarRiel();
    this.franja = this.armarFranja();
    this.historialAgrupado = this.agruparHistorial();
    this.faltas = this.armarFaltas();
    this.gestionarSondeoPuestaEnMarcha();
  }

  // ── Puesta en marcha EN VIVO: sondeo cada 500 ms mientras siga EN_CURSO ──────────────────────────
  // El núcleo la corre en segundo plano; sin esto, la pantalla se queda con la foto del momento en
  // que se aprobó y no se entera de nada hasta que alguien recargue a mano.

  private sondeoPM: ReturnType<typeof setInterval> | null = null;

  /** ALTA-33: cuántos usuarios del portal ya nacieron; si crece, la tabla de credenciales se vuelve a pedir. */
  private usuariosVistos = 0;

  private gestionarSondeoPuestaEnMarcha(): void {
    this.detenerSondeoPuestaEnMarcha();
    const pm = this.sol?.puesta_en_marcha;
    if (this.sol?.estado === 'APROBADA' && pm?.estado === 'EN_CURSO') {
      // ALTA-33: rápido mientras corre; si sólo espera a que Google active la dirección (hasta 90 min), cada 10 s.
      this.sondeoPM = setInterval(() => {
        if (this.esperandoDireccion && Date.now() - this.ultimoSondeo < 10000) return;
        void this.sondearPuestaEnMarcha();
      }, 500);
    }
  }

  private ultimoSondeo = 0;

  private sondearPuestaEnMarcha = async (): Promise<void> => {
    if (!this.sol) return;
    if (this.sondeando) return;
    this.sondeando = true;
    this.ultimoSondeo = Date.now();
    try {
      const pm = await this._sa.puestaEnMarcha(this.id);
      this.sol = { ...this.sol, puesta_en_marcha: pm };
      // El riel y la tarjeta «Terminó el workflow» son campos armados una vez (no getters, para no
      // rearmar la lista entera en cada ciclo): sin esto, se quedan con el texto de antes del sondeo
      // aunque la insignia (que sí es getter) ya muestre el estado nuevo (medido 29-sep, ALTA-000001).
      this.armarVista();
      // Los usuarios del portal nacen mientras la puesta corre: en cuanto aparece uno, se trae la tabla (sin esperar a que termine).
      const nacidos = this.usuariosNacidos(pm);
      if (nacidos > this.usuariosVistos) { this.usuariosVistos = nacidos; void this.traerAccesos(); }
      if (pm.estado !== 'EN_CURSO') {
        this.detenerSondeoPuestaEnMarcha();
        // Al terminar se vuelve a pedir la solicitud entera: las ACCIONES (p. ej. «Descargar las
        // credenciales», que solo existe con la puesta en marcha OPERATIVA) llegan con ella, no con el
        // sondeo liviano. Sin esto el botón no aparecía hasta recargar la página (medido 29-sep).
        void this.cargar();
      }
    } catch {
      // Un sondeo que falla no es un error para avisar (es de fondo): se corta y ya. La próxima
      // acción del usuario (o recargar la pantalla) vuelve a traer el estado real.
      this.detenerSondeoPuestaEnMarcha();
    } finally {
      this.sondeando = false;
    }
  };

  private sondeando = false;

  private detenerSondeoPuestaEnMarcha(): void {
    if (this.sondeoPM !== null) { clearInterval(this.sondeoPM); this.sondeoPM = null; }
  }

  // ── Cabecera ──────────────────────────────────────────────────────────────

  get etapaInsignia(): { texto: string; clase: string } | null {
    const s = this.sol;
    if (!s) return null;
    if (s.estado === 'EN_CURSO' && s.etapa) {
      return { texto: `Etapa ${s.etapa.orden} de ${s.ruta.etapas.length} · ${s.etapa.rol_nombre}`, clase: 'kb-badge-alerta' };
    }
    if (s.estado === 'APROBADA') {
      // Desde hoy manda `puesta_en_marcha` (CONTRATO-PUESTA-EN-MARCHA.md): mientras exista, la
      // insignia cuenta CÓMO va esa puesta en marcha, no el «alta» viejo.
      const pm = s.puesta_en_marcha;
      if (pm) {
        if (pm.estado === 'OPERATIVA') return { texto: 'Operativa', clase: 'kb-badge-exito' };
        if (pm.estado === 'EN_CURSO') return { texto: 'Aprobada · poniéndose en marcha', clase: 'kb-badge-neutro' };
        // FALLO (o `cortada`): faltan ítems por crear.
        const n = this.contarFallosPuesta(pm);
        return { texto: `Aprobada · falta ${n} ítem${n === 1 ? '' : 's'}`, clase: 'kb-badge-alerta' };
      }
      // Solicitudes de antes de este contrato, sin `puesta_en_marcha`: el texto viejo.
      const texto = s.efecto?.estado === 'PENDIENTE' ? 'Aprobada · alta del comercio pendiente' : 'Aprobada';
      return { texto, clase: 'kb-badge-exito' };
    }
    if (s.estado === 'RECHAZADA') return { texto: 'Rechazada', clase: 'kb-badge-peligro' };
    return null;
  }

  /** Cuántos pasos obligatorios (no `NO_APLICA`) quedaron en `FALLO`. */
  private contarFallosPuesta(pm: PuestaEnMarcha): number {
    return pm.grupos.flatMap((g) => g.pasos).filter((p) => p.estado === 'FALLO').length;
  }

  /** Quién la mandó a la etapa donde está — o, si ya la rechazaron, quién la rechazó y cuándo. */
  get mandadaPor(): string {
    const s = this.sol;
    if (!s) return '';
    if (s.estado === 'RECHAZADA') {
      const ev = this.ultimoEvento('RECHAZADA');
      if (ev) return `La rechazó ${this.quienEs(ev)} el ${fechaCorta(ev.at)}.`;
    }
    return s.enviada_por?.username ? `La mandó ${s.enviada_por.username}.` : '';
  }

  private ultimoEvento(tipo: TipoEvento, orden?: number): EventoSolicitud | undefined {
    for (let i = this.eventos.length - 1; i >= 0; i--) {
      const ev = this.eventos[i];
      if (ev.tipo === tipo && (orden === undefined || ev.etapa?.orden === orden)) return ev;
    }
    return undefined;
  }

  /** El riel: las etapas de la ruta, cada una con quién la tiene o quién la hizo y cuándo (el alta no es una etapa). */
  private armarRiel(): PasoRiel[] {
    const s = this.sol;
    if (!s) return [];
    const aRiel: { [k in EtapaRuta['estado']]: PasoRiel['estado'] } = {
      HECHA: 'HECHO', ACTUAL: 'ACTUAL', RECHAZADA: 'FALLO', PENDIENTE: 'PENDIENTE', NO_LLEGO: 'PENDIENTE',
    };
    const pasos: PasoRiel[] = s.ruta.etapas.map((e) => {
      let detalle: string | null = null;
      if (e.estado === 'HECHA') {
        const ev = this.ultimoEvento('APROBADA', e.orden);
        detalle = ev ? `${this.quienEs(ev)} · ${fechaCorta(ev.at)}` : 'Hecha';
      } else if (e.estado === 'ACTUAL') {
        if (s.es_mia && s.responsable) detalle = `La tienes tú · desde ${fechaCorta(s.responsable.desde)}`;
        else if (s.responsable) detalle = `${s.responsable.username} · desde ${fechaCorta(s.responsable.desde)}`;
        else detalle = `Nadie la tomó · desde ${fechaCorta(s.etapa?.desde)}`;
      } else if (e.estado === 'RECHAZADA') {
        const ev = this.ultimoEvento('RECHAZADA', e.orden);
        detalle = ev ? `La rechazó ${this.quienEs(ev)} · ${fechaCorta(ev.at)}` : 'La rechazaron aquí';
      } else if (e.estado === 'NO_LLEGO') {
        detalle = 'No llegó';
      }
      return { clave: `etapa-${e.orden}`, nombre: e.rol_nombre, detalle, estado: aRiel[e.estado] || 'PENDIENTE' };
    });
    // Juan (28-sep): «no me muestres otro paso más». El alta NO es una etapa: pasa sola cuando aprueba el
    // último perfil. Su resultado va en el detalle de esa última etapa: el comercio que nació, o que falló.
    const ultima = pasos[pasos.length - 1];
    if (ultima && s.ruta.fin_estado === 'HECHA') {
      const comercio = s.efecto?.detalle?.comercio;
      const pm = s.puesta_en_marcha;
      if (pm) {
        if (pm.estado === 'OPERATIVA' && comercio) ultima.detalle = `${ultima.detalle || 'Aprobó'} · nació ${comercio.name} (${comercio.code})`;
        else if (pm.estado === 'EN_CURSO') ultima.detalle = `${ultima.detalle || 'Aprobó'} · poniéndose en marcha`;
        else ultima.detalle = `${ultima.detalle || 'Aprobó'} · puesta en marcha con pendientes`; // FALLO o `cortada`
      }
      // Solicitudes de antes de este contrato, sin `puesta_en_marcha`: el texto viejo del alta.
      else if (s.efecto?.estado === 'HECHO' && comercio) ultima.detalle = `${ultima.detalle || 'Aprobó'} · nació ${comercio.name} (${comercio.code})`;
      else if (s.efecto?.estado === 'FALLO') ultima.detalle = `${ultima.detalle || 'Aprobó'} · el alta falló`;
      else ultima.detalle = `${ultima.detalle || 'Aprobó'} · alta pendiente`;
    }
    return pasos;
  }

  // ── «Lo que te toca» ──────────────────────────────────────────────────────

  /** Los puntos de la etapa de quien la tiene, con su estado, sacados de la solicitud: las secciones
   *  que llena su perfil, sus documentos, la visita (si arma el expediente) y, al final, decidir. */
  private puntosDeLaEtapa(): PuntoTarea[] {
    const s = this.sol!;
    const ed = s.secciones_editables;
    const p = (nombre: string, hecho: boolean, detalle: string): PuntoTarea => ({ nombre, detalle, hecho, falta: !hecho });
    const out: PuntoTarea[] = [];
    if (ed.includes('DATOS')) {
      const n = (s.datos.centros_beneficio || []).length;
      out.push(p('Datos del comercio', n > 0, n ? `Con ${n} centro${n === 1 ? '' : 's'} de beneficio` : 'Faltan los centros de beneficio'));
    }
    // ALTA-21: recién creada el alta no hay canales guardados: el paso no está hecho hasta que se marcan.
    if (ed.includes('CANALES')) {
      const marcados = (s.canales || []).length > 0;
      out.push(p('Canales de venta', marcados, marcados ? this.canalesTexto : 'Falta marcarlos'));
    }
    if (ed.includes('CONTACTOS')) {
      const falta = textoFaltanContactos(s.contactos || [], s.canales);
      out.push(p('Contactos', !falta, falta ? falta.replace(/^Faltan?: /, 'Falta ') : this.papelesCargados(s.contactos || [])));
    }
    if (ed.includes('CUENTA')) {
      const c = s.datos.cuenta;
      const ok = !!(c && c.banco && /^\d{20}$/.test(c.numero || ''));
      out.push(p('Cuenta bancaria', ok, ok ? `${c!.banco_nombre} · ${c!.tipo === 'AHORRO' ? 'ahorro' : 'corriente'}` : 'Falta la cuenta'));
    }
    if (ed.includes('DATOS')) {
      // Juan, 2026-10-08: la primera tienda y su caja, del ejecutivo.
      // Juan, 2026-10-19: con su gerente (el núcleo frena la aprobación sin él); la caja es un usuario, no una persona.
      const t = s.datos.tienda;
      const conTienda = !!(t && t.codigo && t.nombre && t.nombre_corto && t.caja);
      const conGente = !!(t?.gerente?.nombre && t?.gerente?.correo);
      const ok = conTienda && conGente;
      out.push(p('Tienda y caja', ok, ok ? `${t!.codigo} · Caja ${t!.caja}` : (conTienda ? 'Falta el gerente' : (t && t.codigo && t.nombre && !t.nombre_corto ? 'Falta el nombre corto de la tienda' : 'Falta la primera tienda'))));
    }
    if (ed.includes('CONDICIONES')) out.push(p('Condiciones', !!s.condiciones, s.condiciones ? 'Guardadas' : 'Faltan el plazo, la comisión y los cobros'));
    const rol = s.etapa?.rol_codigo;
    const docs = (s.documentos || []).filter((d) => d.la_llena === rol);
    if (docs.length) {
      const n = docs.filter((d) => !!d.cargado).length;
      out.push(p(docs.length === 1 ? docs[0].nombre : 'Documentos', n === docs.length, docs.length === 1 ? (n ? 'Cargado' : 'Falta') : `${n} de los ${docs.length} que cargas tú`));   // ALTA-23: la ficha cuenta también los de otros perfiles
    }
    if (ed.includes('DATOS')) {
      const visita = this.eventos.some((e) => e.tipo === 'COMENTARIO' && e.etapa?.orden === s.etapa?.orden);
      out.push(p('La visita', visita, visita ? 'Comentada' : 'Falta el comentario'));
    }
    out.push({ nombre: 'Decidir', detalle: 'Cuando todo lo anterior esté listo', hecho: false, falta: false });
    return out;
  }


  private armarFranja(): Franja | null {
    const s = this.sol;
    if (!s) return null;
    const base: Franja = { rotulo: '', tarea: null, linea: '', extra: null, tono: '', principal: null, secundaria: null, soltar: false, puntos: [] };

    if (s.estado === 'APROBADA') {
      const ev = this.ultimoEvento('APROBADA');
      const quien = ev ? ` La aprobó ${this.quienEs(ev)} el ${fechaCorta(ev.at)}.` : '';
      const aprobadaPor = ev ? `La aprobó ${this.quienEs(ev)} el ${fechaCorta(ev.at)}.` : '';
      const comercio = s.efecto?.detalle?.comercio;
      // Desde hoy manda `puesta_en_marcha` (CONTRATO-PUESTA-EN-MARCHA.md): mientras exista, la
      // tarjeta cuenta CÓMO va esa puesta en marcha (el «Reintentar» de cada ítem vive en la escena
      // de abajo, no acá), no el «alta» viejo.
      const pm = s.puesta_en_marcha;
      if (pm) {
        if (pm.estado === 'EN_CURSO') {
          return {
            ...base, rotulo: 'Terminó el workflow',
            linea: `${aprobadaPor} Se está creando todo lo que el comercio necesita para operar.`,
            tono: '', principal: null,
          };
        }
        if (pm.estado === 'OPERATIVA') {
          const nombre = comercio?.name || 'El comercio';
          return {
            ...base, rotulo: 'Terminó el workflow',
            linea: `${aprobadaPor} ${nombre} ya puede operar.`,
            tono: 'exito', principal: comercio ? 'VER_COMERCIO' : null,
          };
        }
        // FALLO (o `cortada`): faltan ítems por crear.
        const n = this.contarFallosPuesta(pm);
        return {
          ...base, rotulo: 'Terminó el workflow',
          linea: `${aprobadaPor} La puesta en marcha quedó con ${n} ítem${n === 1 ? '' : 's'} sin crear: abajo está lo que falló y su «Reintentar».`,
          tono: 'alerta', principal: null,
        };
      }
      // El efecto de la aprobación (contrato CONTRATO-ALTA-AUTOMATICA.md §2): solicitudes de antes
      // de este contrato, sin `puesta_en_marcha` todavía. El alta del comercio se ejecuta sola al
      // aprobar el aprobador final. HECHO → el comercio y «Ver el comercio»; FALLO → en rojo, con el
      // error, y «Reintentar el alta» si el perfil de quien mira puede.
      if (s.efecto?.estado === 'HECHO' && comercio) {
        return {
          ...base, rotulo: 'Terminó el workflow', tarea: `Alta hecha: el comercio ${comercio.name} (${comercio.code}) ya existe.`,
          linea: `Aprobada.${quien}`, tono: 'exito', principal: 'VER_COMERCIO',
        };
      }
      if (s.efecto?.estado === 'FALLO') {
        return {
          ...base, rotulo: 'Terminó el workflow', tarea: 'El alta del comercio falló.',
          linea: `${s.efecto.detalle?.error || 'No se pudo completar el alta.'}${quien}`, tono: 'peligro',
          principal: s.acciones.reintentar_alta ? 'REINTENTAR_ALTA' : null,
        };
      }
      // NO_APLICA / PENDIENTE: solicitudes de antes de este contrato, que nunca llegaron a ejecutarse.
      const linea = `Aprobada · alta del comercio pendiente.${quien} Se aprobó antes de que el alta fuera automática: se ejecuta con «Reintentar el alta».`;
      return { ...base, rotulo: 'Terminó el workflow', linea, tono: 'exito', principal: s.acciones.reintentar_alta ? 'REINTENTAR_ALTA' : null };
    }
    if (s.estado === 'RECHAZADA') {
      const ev = this.ultimoEvento('RECHAZADA');
      const motivo = ev?.motivo ? ` · motivo: ${this.nombreMotivo(ev.motivo)}` : '';
      const quien = ev ? ` La rechazó ${this.quienEs(ev)} el ${fechaCorta(ev.at)}.` : '';
      return { ...base, rotulo: 'Terminó el workflow', linea: `Rechazada${motivo}.${quien}`, extra: ev?.comentario || null, tono: 'peligro' };
    }
    if (!s.etapa) return null;

    const rol = s.etapa.rol_nombre;
    const devuelta = s.etapa.devuelta_por ? ` La devolvió ${s.etapa.devuelta_por.username} (${s.etapa.devuelta_por.rol_nombre}).` : '';
    // CONTRATO-ADMINISTRADOR.md: «Actúas como <perfil> (administrador)» — el Administrador de altas
    // actúa como el perfil de la etapa; esto lo aclara en cada línea que lo nombra.
    const admin = s.es_admin ? ' (administrador)' : '';

    // No es tu etapa: solo lectura, sin botón.
    if (!s.actuo_como) {
      const quien = s.responsable?.username || 'nadie la tomó';
      return {
        ...base, rotulo: 'Lo que se hace en esta etapa', tarea: s.tarea,
        linea: `Solo lectura: está con el ${rol} (${quien}) desde el ${fechaCorta(s.etapa.desde)}.${devuelta}`,
      };
    }

    const f: Franja = { ...base, rotulo: 'Tarea actual', tarea: s.tarea };
    if (s.acciones.tomar) {
      f.linea = `Está sin asignar y te corresponde como ${s.actuo_como}${admin}: asígnatela para trabajarla.${devuelta}`;
      f.principal = 'TOMAR';
    } else if (s.es_mia) {
      f.puntos = this.puntosDeLaEtapa();
      const listos = f.puntos.filter((p) => p.hecho).length;
      const editables = s.secciones_editables;
      f.rotulo = editables.includes('CONDICIONES') ? 'Tu tarea: pactar las condiciones'
        : editables.length ? 'Tu tarea: armar el expediente' : 'Tu tarea: revisar y decidir';
      f.linea = `${s.actuo_como}${admin} · la tienes tú desde el ${fechaCorta(s.responsable?.desde)}`
        + (f.puntos.length ? ` · ${listos} de ${f.puntos.length} listos` : '') + (devuelta ? `.${devuelta}` : '');
      if (s.secciones_editables.length) { f.principal = 'CONTINUAR'; f.secundaria = this.puedeDecidir ? 'DECIDIR' : null; }
      else { f.principal = 'DECIDIR'; f.secundaria = 'REVISAR'; }
      f.soltar = s.acciones.soltar;
    } else if (s.responsable) {
      f.linea = `Responsable: ${s.responsable.username}, desde el ${fechaCorta(s.responsable.desde)}. Es uno de tus perfiles: puedes leerla, pero decide el responsable.`;
    } else {
      f.linea = s.acciones.motivo || `Te corresponde como ${s.actuo_como}${admin}, pero no puedes asignártela.`;
    }
    return f;
  }

  // ── Expediente (solo lectura) ─────────────────────────────────────────────

  nombrePapel(p: PapelContacto): string { return PAPELES.find((x) => x.codigo === p)?.nombre || p; }
  /** Expuesto tal cual para la plantilla: un solo lugar lee `papel`/`papeles` (service.ts). */
  papelesDe = papelesDe;
  /** «Faltan: el contacto administrador.» — `null` si no falta. */
  get faltanContactos(): string | null { return textoFaltanContactos(this.sol?.contactos || [], this.sol?.canales); }
  /** «Obligatorio: el comercio vende por Integración por API» — `null` si los canales no piden técnico. */
  get tecnicoObligatorio(): string | null { return textoTecnicoObligatorio(this.sol?.canales); }

  quienLlena(sec: Seccion): string { return QUIEN_LLENA[sec]; }

  /** Te toca completar esa sección: la tienes y tu perfil la llena (el núcleo lo dice en `secciones_editables`). */
  puedeCompletar(sec: Seccion): boolean { return !!this.sol?.secciones_editables.includes(sec); }

  pasoDe(sec: Seccion): string { return PASO_DE_SECCION[sec]; }

  /** La sección tiene lo que hace falta: los cuatro datos mínimos, el administrador, todos los
   *  documentos, y las condiciones cargadas. */
  seccionCompleta(sec: Seccion): boolean {
    const s = this.sol;
    if (!s) return true;
    if (sec === 'DATOS') return !!(s.datos.nombre_comercial && s.datos.razon_social && s.datos.rif && fiscalDe(s.datos).street.trim() && fiscalDe(s.datos).city.trim());
    if (sec === 'CANALES') return true;   // las solicitudes viejas no traen `canales`: no se marcan como faltantes
    if (sec === 'CONTACTOS') return !textoFaltanContactos(s.contactos, s.canales);
    if (sec === 'DOCUMENTOS') return (s.documentos || []).every((d) => !!d.cargado);
    if (sec === 'CUENTA') {
      const c = s.datos.cuenta;
      return !!(c && /^\d{20}$/.test(c.numero || '') && c.titular && c.rif_titular);
    }
    return !!s.condiciones;
  }

  /** «Completar» en el encabezado de la sección: solo si te toca a ti y le falta algo. */
  ofreceCompletar(sec: Seccion): boolean { return this.puedeCompletar(sec) && !this.seccionCompleta(sec); }

  get documentosCargados(): number { return (this.sol?.documentos || []).filter((d) => !!d.cargado).length; }

  /** «Pasó sin: X, Y.» — los nombres de la nota que quedó de una aprobación sin documentos. */
  get nombresNotaDocumentos(): string {
    return (this.sol?.nota_documentos?.faltaban || []).map((f) => f.nombre).join(', ');
  }

  urlDocumento(url: string): string { return this._sa.urlDocumento(url); }

  /** Los plazos activos, del más rápido al más lento — mismo criterio que la ficha del comercio. */
  private tarifarioCondiciones = (c: any): any[] =>
    (c?.settlement?.rates || []).filter((r: any) => r?.days && r.status !== false)
      .sort((a: any, b: any) => (Number(a.days) || 0) - (Number(b.days) || 0));

  private plazoDeCondiciones = (c: any): any => {
    const act = this.tarifarioCondiciones(c);
    return act.find((r: any) => r.is_default) || act[0] || null;
  };

  /** PLZ-03: el plazo con su unidad («24 horas», «1 día»). */
  private textoPlazo = (p: any): string => nombreDelPlazo(plazoDe(p || {}));

  /** «7 días al 5 %» — el plazo y la comisión juntos, como «Rige hoy» de la ficha del comercio. */
  plazoYComision(c: any): string {
    const p = this.plazoDeCondiciones(c);
    if (!p) return 'sin definir';
    const valor = p.type_calc === 'FIXED' ? `$ ${p.commission_value}` : `${p.commission_value} %`;
    return `${this.textoPlazo(p)} al ${valor}`;
  }

  riesgoCondiciones(c: any): string {
    const r = c?.risk_model;
    const t = ({ NO_RECOURSE: 'Sin recurso', RECOURSE: 'Con recurso', MIXED: 'Mixto' } as any)[r?.type_risk];
    if (!t) return '—';
    if (r.type_risk === 'RECOURSE' && r.recourse_days) return `${t} · ${r.recourse_days} días`;
    if (r.type_risk === 'MIXED' && r.guarantee_fund_pct) return `${t} · fondo ${r.guarantee_fund_pct} %`;
    return t;
  }

  liquidacionCondiciones(c: any): string {
    const s = c?.settlement;
    if (!s) return '—';
    const momento = s.moment === 'ON_COLLECTION' ? 'a medida que el cliente paga' : s.moment === 'ON_DUE_DATE' ? 'después que vence la cuota'
      : s.moment === 'ON_INSTALLMENT_PAID' ? 'después que el cliente termina de pagar la cuota' : 'al registrarse la venta';
    const lag = s.lag_days ? ` · se paga ${s.lag_days} ${s.lag_days === 1 ? 'día' : 'días'} después del corte` : '';
    return momento + lag;
  }

  /** El cargo por uso primero, después los demás conceptos — el mismo orden que el formulario. */
  /** Juan, 2026-10-08: el modelo de negocio y lo que pasa en día no hábil (antes «Riesgo» y «Liquidación»). */
  modeloNegocioCondiciones(c: any): string {
    const m = c?.business_model === 'COBRANZA' ? 'Servicio de cobranza' : c?.business_model === 'COMPRA_DEUDA' ? 'Cesión de deuda' : '—';
    const regla = c?.settlement?.business_day_rule === 'SAME_DAY' ? 'en día no hábil, ese mismo día' : 'en día no hábil, el hábil siguiente';
    return `${m} · ${regla}`;
  }

  /** ALTA-22: los papeles que de verdad están cargados («Administrador», «Administrador y finanzas»…). */
  private papelesCargados(contactos: any[]): string {
    const n: any = { ADMINISTRADOR: 'administrador', FINANZAS: 'finanzas', TECNICO: 'técnico' };
    const hay = Object.keys(n).filter((k) => contactos.some((c) => papelesDe(c).includes(k as any))).map((k) => n[k]);
    const t = hay.length > 1 ? `${hay.slice(0, -1).join(', ')} y ${hay[hay.length - 1]}` : (hay[0] || 'cargados');
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  get canalesTexto(): string {
    const n: any = { PORTAL: 'Portal', CAJA: 'Caja', LINK_PAGO: 'Link de pago', INTEGRACION: 'API', TIENDA_EN_LINEA: 'Tienda en línea' };
    // Juan, 2026-10-19: «vender como cajero no es una modalidad»: el canal CAJA sigue guardado, pero no se nombra.
    return (this.sol?.canales || []).filter((c) => c !== 'CAJA').map((c) => n[c] || c).join(' · ') || 'ninguno';
  }

  cobrosCondiciones(c: any): string {
    const partes: string[] = [];
    const f = c?.flat_fee;
    if (f?.value) partes.push(`Cargo por uso ${f.value}${f.type_calc === 'PERCENTAGE' ? ' % de la venta' : ' USD'}${f.charged_with === 'INITIAL' ? ', con la inicial' : ', al liquidar'}`);
    for (const co of (c?.charges || []).filter((x: any) => x?.concept)) {
      const valor = co.type_calc === 'FIXED' ? `${co.value} fijo` : `${co.value} %`;
      partes.push(`${co.concept} ${valor}${co.status === false ? ' (pausado)' : ''}`);
    }
    return partes.length ? partes.join(', ') : 'ninguno';
  }

  private readonly NOMBRES_MODELOS: any = {
    PORTAL: 'Portal del comercio', CAJA: 'Caja', LINK_PAGO: 'Link de pago',
    INTEGRACION: 'Integración por API', TIENDA_EN_LINEA: 'Tienda en línea',
  };

  /** Solo los que se marcan «la usa». Queda pactado (regla de Juan, 2026-09-28): la liquidación
   *  todavía no descuenta el cobro por modelo — se dice aparte, en la plantilla. */
  modelosCondiciones(c: any): string {
    const usados = (c?.modelos || []).filter((m: any) => m?.usa);
    if (!usados.length) return 'ninguno marcado';
    return usados.map((m: any) => {
      const nombre = this.NOMBRES_MODELOS[m.codigo] || m.codigo;
      if (!m.cobra) return `${nombre} (sin cobro)`;
      const forma = m.cargo?.type_calc === 'PERCENTAGE' ? '%' : '';
      return `${nombre} (${m.cargo?.value ?? '—'}${forma})`;
    }).join(', ');
  }

  // ── Tomar / soltar ────────────────────────────────────────────────────────

  tomar = async (): Promise<void> => {
    if (!this.sol?.acciones.tomar) return;
    this.tomando = true; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    try {
      await this._sa.tomar(this.id);
      await this.recargar('La tomaste.');
    } catch (e: any) {
      this.fallo(e, 'No se pudo tomar la solicitud.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.tomando = false;
    }
  };

  soltar = async (): Promise<void> => {
    if (!this.sol?.acciones.soltar) return;
    this.soltando = true; this.confirmarSoltar = false; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    try {
      await this._sa.soltar(this.id);
      await this.recargar('La soltaste: vuelve a la bandeja.');
    } catch (e: any) {
      this.fallo(e, 'No se pudo soltar la solicitud.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.soltando = false;
    }
  };

  // ── Reintentar el alta (contrato CONTRATO-ALTA-AUTOMATICA.md §2: efecto en FALLO) ───────────────

  reintentando = false;

  reintentarAlta = async (): Promise<void> => {
    if (!this.sol?.acciones.reintentar_alta) return;
    this.reintentando = true; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    try {
      await this._sa.reintentarAlta(this.id);
      await this.recargar('Se reintentó el alta del comercio.');
    } catch (e: any) {
      this.fallo(e, 'No se pudo reintentar el alta.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.reintentando = false;
    }
  };

  // ── Reintentar la puesta en marcha (CONTRATO-PUESTA-EN-MARCHA.md, «B») ──────────────────────────

  reintentandoPuestaEnMarcha = false;

  reintentarPuestaEnMarcha = async (): Promise<void> => {
    if (!this.sol?.acciones.reintentar_puesta_en_marcha) return;
    this.reintentandoPuestaEnMarcha = true; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    try {
      await this._sa.reintentarPuestaEnMarcha(this.id);
      await this.recargar('Se reintentó la puesta en marcha.');
    } catch (e: any) {
      this.fallo(e, 'No se pudo reintentar la puesta en marcha.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.reintentandoPuestaEnMarcha = false;
    }
  };

  // ── Completar el domicilio (un paso falló por un dato incompleto: `completar` en el paso) ──────────

  completandoDomicilio = false;
  guardandoDomicilio = false;
  errorDomicilio = '';
  formDomicilio: Required<DomicilioFiscal> = fiscalDe(null);

  abrirCompletarDomicilio = (): void => {
    this.formDomicilio = fiscalDe(this.sol?.datos);
    this.errorDomicilio = '';
    this.completandoDomicilio = true;
  };

  cancelarCompletarDomicilio = (): void => { this.completandoDomicilio = false; this.errorDomicilio = ''; };

  get domicilioListo(): boolean { return !!this.formDomicilio.street.trim() && !!this.formDomicilio.city.trim(); }

  guardarYReintentarDomicilio = async (): Promise<void> => {
    if (!this.sol?.acciones.reintentar_puesta_en_marcha || this.guardandoDomicilio) return;
    this.guardandoDomicilio = true; this.errorDomicilio = ''; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    try {
      await this._sa.completarPuesta(this.id, fiscalSinVacios(this.formDomicilio));
      this.completandoDomicilio = false;
      await this.recargar('Se guardó el domicilio y se reintentó la puesta en marcha.');
    } catch (e: any) {
      this.errorDomicilio = mensajeDeError(e, 'No se pudo guardar el domicilio.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.guardandoDomicilio = false;
    }
  };

  /** El «Reintentar» de UN ítem (cambio «E»): rehace ese paso y los que dependen de él. */
  reintentandoPaso: string | null = null;

  reintentarPasoClick = async (clave: string): Promise<void> => {
    if (!this.sol?.acciones.reintentar_puesta_en_marcha || this.reintentandoPaso) return;
    this.reintentandoPaso = clave; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    try {
      await this._sa.reintentarPaso(this.id, clave);
      await this.recargar('Se reintentó el paso.');
    } catch (e: any) {
      this.fallo(e, 'No se pudo reintentar el paso.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.reintentandoPaso = null;
    }
  };

  // ── Entregar las credenciales (CONTRATO-PUESTA-EN-MARCHA.md, «K») ───────────────────────────────
  // Se arman AL ENTREGAR y no se guardan; volver a entregarlas RENUEVA las claves — por eso confirma
  // en la misma página, sin modal, antes de llamar al servicio y descargar el PDF.

  /** Se pueden descargar las credenciales de acceso: cuando el núcleo da la acción, o en cuanto ya existen usuarios del portal
   *  aunque la puesta siga en curso o esperando a Google (Juan, 2026-10-23). Si todavía no se puede, el núcleo responde 409 y se muestra su mensaje. */
  get puedeEntregarCredenciales(): boolean {
    return !!this.sol?.acciones?.entregar_credenciales || (!!this.sol?.puesta_en_marcha && this.accesos.length > 0);
  }

  /** Qué credenciales se están por descargar: las de ACCESO (portal, para el comercio) o las de INTEGRACIÓN (pasarela, uso interno). */
  confirmandoCredenciales: 'acceso' | 'integracion' | null = null;
  entregandoCredenciales = false;
  /** Cuándo se entregaron en ESTA sesión de la pantalla (no viene del núcleo: no se guarda). */
  credencialesEntregadasEn: { acceso?: string; integracion?: string } = {};

  pedirEntregarCredenciales = (parte: 'acceso' | 'integracion' = 'acceso'): void => {
    if (!this.puedeEntregarCredenciales || this.entregandoCredenciales) return;
    this.confirmandoCredenciales = parte;
  };

  cancelarEntregarCredenciales = (): void => {
    this.confirmandoCredenciales = null;
  };

  confirmarEntregarCredenciales = async (): Promise<void> => {
    const parte = this.confirmandoCredenciales;
    if (!parte || !this.puedeEntregarCredenciales || this.entregandoCredenciales) return;
    this.entregandoCredenciales = true; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    try {
      const { blob, nombre } = await this._sa.entregarCredenciales(this.id, parte);
      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = nombre;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      // Se suelta en el siguiente turno del navegador: revocarlo enseguida aborta la descarga en Firefox.
      setTimeout(() => URL.revokeObjectURL(url), 0);
      this.credencialesEntregadasEn = { ...this.credencialesEntregadasEn, [parte]: new Date().toISOString() };
      this.confirmandoCredenciales = null;
      // Descargar renueva las tres y las manda por correo: la tabla cambia (Juan, 2026-10-19, decisión b).
      if (parte === 'acceso') await this.traerAccesos();
    } catch (e: any) {
      this.fallo(e, 'No se pudieron entregar las credenciales.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.entregandoCredenciales = false;
    }
  };

  // ── Los accesos al portal que nacieron con el alta (Juan, 2026-10-19) ───────
  // Administración, gerente de la tienda y caja (su clave va al gerente): a quién se le mandó y cuándo. Nadie de CrediTotal
  // ve las claves: sólo salen en el PDF. «Renovar» de una fila renueva sólo ésa y se la manda a esa persona.

  accesos: AccesoAlta[] = [];
  puedeRenovarAccesos = false;
  /** El `usuario_id` de la fila que pide confirmar «Renovar», o null. */
  confirmandoRenovar: string | null = null;
  renovandoAcceso = false;
  avisoAccesos = '';

  /** La dirección propia del portal sigue esperando a que Google la active: los correos de acceso aún no salen. */
  get esperandoDireccion(): boolean {
    const pm: any = this.sol?.puesta_en_marcha;
    const pasos: any[] = [...(pm?.pasos || []), ...((pm?.grupos || []).flatMap((g: any) => g.pasos || []))];
    return pasos.some((p) => p.clave === 'DIRECCION_PROPIA' && p.estado === 'ESPERANDO');
  }
  private usuariosNacidos(pm: any): number {
    const pasos: any[] = [...(pm?.pasos || []), ...((pm?.grupos || []).flatMap((g: any) => g.pasos || []))];
    return pasos.filter((p) => /^USUARIO_/.test(p.clave) && (p.estado === 'VERIFICADO' || p.estado === 'HECHO')).length;
  }
  /** «Puede operar desde dd/mm/aaaa», si la solicitud trae `opera_desde`. */
  get operaDesde(): string {
    const d = (this.sol as any)?.opera_desde;
    return d ? String(d).slice(0, 10).split('-').reverse().join('/') : '';
  }

  private async traerAccesos(): Promise<void> {
    if (!this.sol?.acciones.entregar_credenciales && !this.sol?.puesta_en_marcha) { this.accesos = []; return; }
    try {
      const r = await this._sa.accesos(this.id);
      this.accesos = r.accesos || [];
      this.puedeRenovarAccesos = !!r.puede_renovar;
    } catch (e) {
      this.accesos = [];   // (403 sin la acción incluido: se oculta) la tabla es un extra de la tarjeta: si no llega, quedan el PDF y su texto
    }
  }

  /** «Administrador del portal», «Gerente de la tienda PC-01», «Caja 1» (la caja no es una persona). */
  papelDelAcceso = (a: AccesoAlta): string => {
    const t = this.sol?.datos.tienda;
    if (a.papel === 'GERENTE') return t?.codigo ? `Gerente de la tienda ${t.codigo}` : 'Gerente de la tienda';
    if (a.papel === 'VENDEDOR') return 'Vendedor (link de pago)';
    if (a.papel === 'CAJA') return t?.caja ? `Caja ${t.caja}` : 'Caja';
    return 'Administrador del portal';
  };

  pedirRenovar = (a: AccesoAlta): void => {
    if (!this.puedeRenovarAccesos || this.renovandoAcceso) return;
    this.avisoAccesos = '';
    this.confirmandoRenovar = String(a.usuario_id);
  };

  cancelarRenovar = (): void => { this.confirmandoRenovar = null; };

  confirmarRenovar = async (a: AccesoAlta): Promise<void> => {
    if (this.renovandoAcceso) return;
    this.renovandoAcceso = true; this.error = ''; this.detalle = []; this.avisoAccesos = '';
    try {
      const r = await this._sa.renovarAcceso(this.id, a.usuario_id);
      this.avisoAccesos = r.envio_estado === 'enviado'
        ? `Clave de ${a.username} renovada y enviada a ${a.correo}.`
        : `Se renovó la clave de ${a.username} (la anterior ya no sirve), pero no se pudo mandar el correo${r.envio_error ? ` (${r.envio_error})` : ''}. Vuelva a intentarlo o descargue el PDF.`;
      this.confirmandoRenovar = null;
      await this.traerAccesos();
    } catch (e: any) {
      this.fallo(e, 'No se pudo renovar la clave.');
    } finally {
      this.renovandoAcceso = false;
    }
  };

  // ── Comentar ──────────────────────────────────────────────────────────────

  get comentarioListo(): boolean { return this.comentarioTexto.trim().length > 0; }

  /** Cuando no puedes comentar: quién sí puede, en una frase. */
  get quienComenta(): string {
    const s = this.sol;
    if (!s) return '';
    if (s.estado !== 'EN_CURSO' || !s.etapa) return 'La solicitud terminó: ya no se comenta.';
    if (s.responsable && !s.es_mia) return `Comenta el responsable: ${s.responsable.username} (${s.etapa.rol_nombre}).`;
    if (!s.responsable) {
      return s.acciones.tomar ? 'Comenta el responsable: asígnatela para comentar.' : `Comenta el responsable, que tiene que ser ${s.etapa.rol_nombre}.`;
    }
    return s.acciones.motivo || 'No puedes comentar en esta etapa.';
  }

  comentar = async (): Promise<void> => {
    if (!this.comentarioListo) return;
    this.comentando = true; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    try {
      await this._sa.comentar(this.id, this.comentarioTexto.trim());
      this.comentarioTexto = ''; this.guardarComentarios();
      await this.recargar('Comentario agregado.');
    } catch (e: any) {
      this.fallo(e, 'No se pudo agregar el comentario.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.comentando = false;
    }
  };

  // ── Decidir ───────────────────────────────────────────────────────────────

  /** El botón que abre la carga de la etapa: dice qué se carga (glosario: «Cargar el expediente» / «Cargar las condiciones»). */
  /** El botón principal de la tarea: «Cargar…» si no hay nada hecho, «Continuar…» si ya se empezó. */
  get textoPrincipal(): string {
    const empezada = (this.franja?.puntos || []).some((p) => p.hecho);
    const que = this.sol?.secciones_editables.includes('CONDICIONES') ? 'las condiciones' : 'el expediente';
    return `${empezada ? 'Continuar' : 'Cargar'} ${que}`;
  }

  get textoCargar(): string {
    return this.sol?.secciones_editables.includes('CONDICIONES') ? 'Cargar las condiciones' : 'Cargar el expediente';
  }

  /** Quién carga los documentos: el ejecutivo los suyos y el gerente el contrato firmado (`la_llena` de cada uno). */
  get quienCargaDocumentos(): string {
    const nombre: Record<string, string> = { EJECUTIVO_COMERCIAL: 'el Ejecutivo comercial', GERENTE_COMERCIAL: 'el Gerente comercial' };
    const docs = this.sol?.documentos || [];
    const contrato = docs.find((d: any) => d.codigo === 'CONTRATO_FIRMADO');
    if (!contrato || !contrato.la_llena || contrato.la_llena === 'EJECUTIVO_COMERCIAL') return `los carga ${nombre['EJECUTIVO_COMERCIAL']}`;
    return `los carga ${nombre['EJECUTIVO_COMERCIAL']}; el contrato firmado, ${nombre[contrato.la_llena] || 'el Gerente comercial'}`;
  }

  get puedeDecidir(): boolean {
    const a = this.sol?.acciones;
    return !!a && (a.aprobar || a.devolver || a.rechazar);
  }

  /** Cuando no puedes decidir: por qué, en una frase. */
  get porQueNoDecide(): string {
    const s = this.sol;
    if (!s) return '';
    if (s.estado !== 'EN_CURSO' || !s.etapa) return 'La solicitud terminó: no queda nada por decidir.';
    if (!s.actuo_como) return `Decide el responsable, que tiene que ser ${s.etapa.rol_nombre}; tú no tienes ese perfil.`;
    if (s.responsable && !s.es_mia) return `Decide el responsable: ${s.responsable.username}.`;
    if (s.acciones.tomar) return 'Para decidir, primero asígnatela desde «Tarea actual».';
    return s.acciones.motivo || 'No puedes decidir en esta etapa.';
  }

  /** Lo que frena la aprobación y los documentos que faltan, con el paso que lo arregla. */
  private armarFaltas(): Falta[] {
    const s = this.sol;
    if (!s || !this.puedeDecidir) return [];
    const seccionDe = (t: string): Seccion | null =>
      /contacto/i.test(t) ? 'CONTACTOS' : /condici/i.test(t) ? 'CONDICIONES' : /documento/i.test(t) ? 'DOCUMENTOS'
        : /cuenta banc/i.test(t) ? 'CUENTA'
        : /dato|rif|raz[oó]n|direcci/i.test(t) ? 'DATOS' : null;
    const aFalta = (texto: string, sec: Seccion | null): Falta => ({
      texto,
      paso: sec && this.puedeCompletar(sec) ? PASO_DE_SECCION[sec] : null,
      quien: sec && !this.puedeCompletar(sec) ? QUIEN_LLENA[sec] : null,
    });
    const lista = (s.falta_para_aprobar || []).map((t) => aFalta(t, seccionDe(t)));
    if (this.documentosFaltantesParaAprobar.length) {
      lista.push(aFalta(`Faltan documentos: ${this.nombresDocumentosFaltantes}. No frenan: aprobar sin ellos pide una nota.`, 'DOCUMENTOS'));
    }
    return lista;
  }

  /** Elige la primera opción posible cuando cambian las acciones (carga o después de decidir). */
  private prepararDecision(): void {
    const a = this.sol?.acciones;
    if (!a) { this.decision = ''; this.confirmandoRechazo = false; return; }
    const sigueValida = (this.decision === 'APROBAR' && a.aprobar)
      || (this.decision === 'DEVOLVER' && a.devolver)
      || (this.decision === 'RECHAZAR' && a.rechazar);
    if (!sigueValida) {
      this.decision = a.aprobar ? 'APROBAR' : a.devolver ? 'DEVOLVER' : a.rechazar ? 'RECHAZAR' : '';
      this.confirmandoRechazo = false;
    }
  }

  elegirDecision(d: Decision): void {
    if (!this.opcionHabilitada(d)) return;
    this.decision = d;
    this.confirmandoRechazo = false;
  }

  opcionHabilitada(d: Decision): boolean {
    const a = this.sol?.acciones;
    if (!a) return false;
    if (d === 'APROBAR') return a.aprobar;
    if (d === 'DEVOLVER') return a.devolver;
    return a.rechazar;
  }

  /** A dónde pasa si se aprueba: el siguiente perfil de la ruta, o el fin. */
  get siguienteEtapaNombre(): string {
    const s = this.sol; if (!s || !s.etapa) return '';
    const etapas = s.ruta.etapas;
    const i = etapas.findIndex((e) => e.orden === s.etapa?.orden);
    if (i === -1) return s.ruta.fin;
    return i < etapas.length - 1 ? etapas[i + 1].rol_nombre : s.ruta.fin;
  }

  /** Es la última etapa de la ruta: aprobar no pasa a otro perfil, termina el workflow (H-09). */
  get esUltimaEtapa(): boolean {
    const s = this.sol; if (!s || !s.etapa) return false;
    const etapas = s.ruta.etapas;
    const i = etapas.findIndex((e) => e.orden === s.etapa?.orden);
    return i === -1 || i === etapas.length - 1;
  }

  /** En quién cae si se devuelve: `devuelve_a` de la etapa actual, leído en la propia ruta. */
  get devuelveAEtapaNombre(): string {
    const s = this.sol; if (!s || !s.etapa) return '';
    const actual = s.ruta.etapas.find((e) => e.orden === s.etapa?.orden);
    if (!actual || actual.devuelve_a === null) return '';
    const destinoOrden = actual.devuelve_a;   // copiado a primitivo: evita perder el narrowing en el callback
    return s.ruta.etapas.find((e) => e.orden === destinoOrden)?.rol_nombre || '';
  }

  get comentarioDecisionValido(): boolean { return this.comentarioDecision.trim().length >= 3; }

  /** Cambio de Juan (§5): los documentos `para: 'APROBAR'` NO frenan la aprobación — piden una nota
   *  marginal en su lugar. Solo aplica cuando la decisión elegida es Aprobar. */
  get documentosFaltantesParaAprobar(): Array<{ codigo: string; nombre: string }> {
    return this.sol?.documentos_faltantes || [];
  }

  get nombresDocumentosFaltantes(): string {
    return this.documentosFaltantesParaAprobar.map((d) => d.nombre).join(', ');
  }

  get notaDocumentosRequerida(): boolean {
    return this.decision === 'APROBAR' && this.documentosFaltantesParaAprobar.length > 0;
  }

  get notaDocumentosValida(): boolean {
    return !this.notaDocumentosRequerida || this.notaDocumentos.trim().length >= 10;
  }

  // ── Desde cuándo opera el comercio (Juan, 2026-10-08: lo elige el aprobador final, no el gerente) ──
  /** Hoy en Caracas, «AAAA-MM-DD». */
  get hoyCaracas(): string { return new Date(Date.now() - 4 * 3600 * 1000).toISOString().slice(0, 10); }
  rigeDesde = '';
  get rigeDesdeValida(): boolean { return /^\d{4}-\d{2}-\d{2}$/.test(this.rigeDesde) && this.rigeDesde >= this.hoyCaracas; }
  get rigeDesdeTexto(): string { return this.rigeDesde ? this.rigeDesde.split('-').reverse().join('/') : ''; }

  get decisionValida(): boolean {
    const s = this.sol;
    if (!s || !this.decision || !this.comentarioDecisionValido) return false;
    if (this.decision === 'APROBAR') return s.acciones.aprobar && s.falta_para_aprobar.length === 0 && this.notaDocumentosValida
      && (!s.pide_rige_desde || this.rigeDesdeValida);
    if (this.decision === 'DEVOLVER') return s.acciones.devolver;
    return s.acciones.rechazar && !!this.motivoRechazo;
  }

  get textoBotonDecision(): string {
    if (this.decision === 'APROBAR') {
      return this.esUltimaEtapa ? 'Aprobar y terminar el workflow' : `Aprobar y pasar al ${this.siguienteEtapaNombre || 'siguiente'}`;
    }
    if (this.decision === 'DEVOLVER') return `Devolver al ${this.devuelveAEtapaNombre || 'perfil anterior'}`;
    if (this.decision === 'RECHAZAR') return 'Rechazar la solicitud';
    return 'Decidir';
  }

  /** El renglón bajo cada opción de «Tu decisión» (H-08, H-09): si la opción está apagada, dice por
   *  qué; si no, a quién va. */
  get textoOpcionAprobar(): string {
    if (this.esUltimaEtapa) return 'Termina el workflow: nace el comercio con todo lo cargado.';   // ALTA-39
    return `Pasa a ${this.siguienteEtapaNombre || this.sol?.ruta.fin || 'la siguiente etapa'}.`;
  }

  /** «Rechazan: X y Z.» — para las opciones apagadas de «Tu decisión» (H-03): dice QUIÉN sí puede,
   *  no solo que este perfil no puede. */
  private unirConY(lista: string[]): string {
    if (lista.length === 0) return '';
    if (lista.length === 1) return lista[0];
    return `${lista.slice(0, -1).join(', ')} y ${lista[lista.length - 1]}`;
  }

  get textoOpcionDevolver(): string {
    if (this.opcionHabilitada('DEVOLVER')) return `Cae en ${this.devuelveAEtapaNombre || 'la etapa anterior'}; corregida, vuelve a pasar por ti.`;
    const s = this.sol;
    // H-03: en la primera etapa no hay a quién devolver (es estructural, no del perfil); en
    // cualquier otra, es que este perfil no devuelve.
    const esPrimeraEtapa = !!s?.etapa && s.ruta.etapas.length > 0 && s.etapa.orden === s.ruta.etapas[0].orden;
    return esPrimeraEtapa ? 'Es la primera etapa: no hay a quién devolver.' : 'Este perfil no devuelve.';
  }

  get textoOpcionRechazar(): string {
    if (this.opcionHabilitada('RECHAZAR')) return 'Termina la solicitud. Lleva el motivo.';
    // H-03: quién sí rechaza en este workflow, leído de `ruta.etapas[].puede_rechazar`.
    const quienes = (this.sol?.ruta.etapas || []).filter((e) => e.puede_rechazar).map((e) => e.rol_nombre);
    return quienes.length ? `Rechazan: ${this.unirConY(quienes)}.` : 'En este workflow nadie rechaza.';
  }

  /** Rechazar pide confirmación en dos pasos, en la misma línea; aprobar y devolver van directo. */
  alDecidir(): void {
    if (!this.decisionValida) return;
    if (this.decision === 'RECHAZAR' && !this.confirmandoRechazo) { this.confirmandoRechazo = true; return; }
    this.ejecutarDecision();
  }

  cancelarRechazo(): void { this.confirmandoRechazo = false; }

  private ejecutarDecision = async (): Promise<void> => {
    if (!this.decision) return;
    this.decidiendo = true; this.error = ''; this.detalle = []; this.aviso = ''; this.avisoConVolver = false;
    const decisionQueSeMando = this.decision;
    const notaQueSeMando = this.notaDocumentosRequerida ? this.notaDocumentos.trim() : undefined;
    try {
      const r = await this._sa.decidir(
        this.id, decisionQueSeMando as Decision, this.comentarioDecision.trim(),
        decisionQueSeMando === 'RECHAZAR' ? this.motivoRechazo : undefined,
        notaQueSeMando,
        decisionQueSeMando === 'APROBAR' && this.sol?.pide_rige_desde ? this.rigeDesde : undefined,
      );
      this.comentarioDecision = ''; this.motivoRechazo = ''; this.notaDocumentos = ''; this.confirmandoRechazo = false; this.guardarComentarios();
      // El aviso dice qué pasó DE VERDAD, con lo que devolvió el núcleo: a quién pasó o cómo terminó.
      const destino = r?.etapa?.rol_nombre;
      const avisoOk = decisionQueSeMando === 'RECHAZAR' ? 'La rechazaste: la solicitud terminó.'
        : r?.estado === 'APROBADA' ? (r?.efecto?.estado === 'HECHO' ? `La aprobaste: terminó el workflow y nació el comercio ${r?.efecto?.detalle?.comercio?.name || ''} (${r?.efecto?.detalle?.comercio?.code || ''}).` : r?.efecto?.estado === 'FALLO' ? 'La aprobaste, pero el alta del comercio no se pudo completar: mira el error y usa «Reintentar el alta».' : 'La aprobaste: terminó el workflow.')
        : decisionQueSeMando === 'APROBAR' ? `La aprobaste y pasó al ${destino || 'perfil siguiente'}.`
        : `La devolviste: cayó en el ${destino || 'perfil anterior'}.`;
      await this.recargar(avisoOk);
      this.avisoConVolver = true;   // ya decidiste: no queda ninguna acción tuya, ofrece el camino de vuelta
      // Juan, 08-oct: al terminar el workflow se salta a la Ficha, con la puesta en marcha al frente.
      if (r?.estado === 'APROBADA') {
        this.irA('expediente');
        setTimeout(() => document.querySelector('app-puesta-en-marcha-escena')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
      }
    } catch (e: any) {
      this.fallo(e, 'No se pudo registrar la decisión.');
      await this.refrescarSiConflicto(e);
    } finally {
      this.decidiendo = false;
    }
  };

  // ── Historial ─────────────────────────────────────────────────────────────

  /** Un encabezado «Etapa N · rol» cada vez que cambia la etapa del evento, en el orden que llega. */
  private agruparHistorial(): Array<{ cabecera: string | null; ev: EventoSolicitud }> {
    let ultimoOrden: number | null = null;
    return this.eventos.map((ev) => {
      let cabecera: string | null = null;
      if (ev.etapa && ev.etapa.orden !== ultimoOrden) {
        cabecera = `Etapa ${ev.etapa.orden} · ${ev.etapa.rol_nombre}`;
        ultimoOrden = ev.etapa.orden;
      }
      return { cabecera, ev };
    });
  }

  fechaHora = fechaHora;

  quienEs(ev: EventoSolicitud): string {
    let base = 'Alguien';
    if (ev.actor?.username) base = ev.actor.username;
    else if (ev.actor?.user) base = ev.actor.user;
    else if (ev.actor?.kind === 'SISTEMA') return 'El sistema';
    else if (ev.actor?.kind === 'CLIENTE' || ev.actor?.kind === 'COMERCIO') return 'El comercio';
    // CONTRATO-ADMINISTRADOR.md: «<usuario> (administrador) aprobó como Gerente comercial».
    return ev.actor?.como_admin ? `${base} (administrador)` : base;
  }

  /** «como Gerente comercial» — solo en el evento de quien lo hizo como Administrador de altas. */
  comoPerfilAdmin(ev: EventoSolicitud): string {
    return (ev.actor?.como_admin && ev.actor?.actuo_como) ? ` como ${ev.actor.actuo_como}` : '';
  }

  esDecision(tipo: TipoEvento): boolean { return tipo === 'APROBADA' || tipo === 'DEVUELTA' || tipo === 'RECHAZADA'; }

  // ── El historial (Juan, 2026-10-08): qué aprobó cada perfil ────────────────────────────────

  /** Cómo se pinta cada movimiento: tarjeta (decisión), resultado (puesta en marcha), línea gris u oculto. */
  tipoDeRenglon(ev: EventoSolicitud): 'decision' | 'puesta' | 'menor' | 'oculto' {
    if (this.esDecision(ev.tipo)) return 'decision';
    if ((ev.tipo as string) === 'PUESTA_EN_MARCHA') return 'puesta';
    // «Terminó el workflow» y «Nació el comercio» ya los dice la tarjeta del aprobador.
    if (ev.tipo === 'FINALIZADA' || (ev.tipo === 'ALTA' && ev.data?.estado === 'HECHO')) return 'oculto';
    return 'menor';
  }

  insigniaDecision(tipo: TipoEvento): string {
    return tipo === 'APROBADA' ? 'Aprobó' : tipo === 'DEVUELTA' ? 'Devolvió' : 'Rechazó';
  }

  private readonly NOMBRE_CANAL: Record<string, string> = {
    PORTAL: 'Portal', CAJA: 'Caja', LINK_PAGO: 'Link de pago', INTEGRACION: 'API', TIENDA_EN_LINEA: 'Tienda en línea',
  };
  private readonly NOMBRE_PAPEL: Record<string, string> = {
    ADMINISTRADOR: 'administrador', FINANZAS: 'finanzas', TECNICO: 'técnico', OTRO: 'otro',
  };
  private cacheResumen = new Map<string, { titulo: string; datos: Array<{ et: string; val: string; falta?: boolean }> }>();

  /** Lo que dejó listo el perfil al decidir, de la foto que guardó el núcleo (`data.resumen`). */
  resumenDecision(ev: EventoSolicitud): { titulo: string; datos: Array<{ et: string; val: string; falta?: boolean }> } {
    const ya = this.cacheResumen.get(ev._id);
    if (ya) return ya;
    const r = ev.data?.resumen || {};
    const datos: Array<{ et: string; val: string; falta?: boolean }> = [];
    let titulo = 'Lo que dejó listo';
    if (r.comercio) {
      titulo = 'El expediente que dejó listo';
      datos.push({ et: 'Comercio', val: [r.comercio.razon_social, r.comercio.rif].filter(Boolean).join(' · ') || '—' });
    }
    if (r.centros_beneficio) {
      const n = (r.centros_beneficio as string[]).map((c) => this.centros.find((x) => x.codigo === c)?.nombre || c);
      datos.push({ et: 'Centros de beneficio', val: n.join(', ') || 'ninguno', falta: !n.length });
    }
    if (r.canales) datos.push({ et: 'Canales de venta', val: (r.canales as string[]).filter((x) => x !== 'CAJA').map((x) => this.NOMBRE_CANAL[x] || x).join(' · ') });
    if (r.contactos) {
      const p = (r.contactos as string[]).map((c) => this.NOMBRE_PAPEL[c] || c);
      datos.push({ et: 'Contactos', val: p.length ? p.join(', ').replace(/^./, (x) => x.toUpperCase()) : 'ninguno', falta: !p.length });
    }
    if (r.tienda) datos.push({ et: 'Tienda y caja', val: `${r.tienda.codigo} · ${r.tienda.nombre}${r.tienda.nombre_corto ? ` (${r.tienda.nombre_corto})` : ''} · Caja ${r.tienda.caja}` });
    if (r.cuenta) datos.push({ et: 'Cuenta bancaria', val: [r.cuenta.banco, (r.cuenta.tipo || '').toLowerCase()].filter(Boolean).join(' · ') });
    if (r.condiciones) {
      titulo = 'Las condiciones que pactó';
      const c = r.condiciones;
      const dias = c.plazo?.dias ? c.plazo.dias + ' días' : null;
      datos.push({ et: 'Plazo y comisión', val: [c.plazo?.nombre !== dias ? c.plazo?.nombre : null, dias, c.plazo?.comision != null ? c.plazo.comision + ' %' : null].filter(Boolean).join(' · ') });
      if (c.cargo) {
        const monto = c.cargo.tipo === 'PERCENTAGE' ? `${c.cargo.valor} % de la venta` : `${c.cargo.valor} USD`;
        datos.push({ et: 'Cargo por uso', val: `${monto}, ${c.cargo.con === 'INITIAL' ? 'con la inicial' : 'al liquidar'}` });
      }
      if (c.modelo_negocio) datos.push({ et: 'Esquema de liquidación', val: c.modelo_negocio === 'COBRANZA' ? 'Servicio de cobranza' : 'Cesión de deuda' });
      if (c.canales) datos.push({ et: 'Canales de venta', val: (c.canales as string[]).filter((x) => x !== 'CAJA').map((x) => this.NOMBRE_CANAL[x] || x).join(' · ') });
      if (c.contrato) datos.push({ et: 'Contrato firmado', val: c.contrato.cargado ? (c.contrato.referencia || 'Cargado') : 'Falta', falta: !c.contrato.cargado });
    }
    if (r.documentos) datos.push({ et: 'Documentos', val: `${r.documentos.cargados} de ${r.documentos.total} cargados`, falta: r.documentos.cargados < r.documentos.total });
    if (r.opera_desde) {
      titulo = 'Lo que decidió';
      datos.push({ et: 'El comercio opera desde', val: String(r.opera_desde).slice(0, 10).split('-').reverse().join('/') });
      const cm = this.sol?.efecto?.detalle?.comercio;
      if (cm) datos.push({ et: 'Nació el comercio', val: `${cm.code} · ${cm.name}` });
    }
    const salida = { titulo, datos };
    this.cacheResumen.set(ev._id, salida);
    return salida;
  }

  /** Los nombres de los documentos con que pasó sin tenerlos. */
  docsFaltantesTexto(ev: EventoSolicitud): string {
    const cods: string[] = ev.data?.documentos_faltantes || [];
    return cods.map((c) => (this.sol?.documentos || []).find((d) => d.codigo === c)?.nombre?.toLowerCase() || c).join(', ');
  }

  tituloPuesta(ev: EventoSolicitud): string {
    const d = ev.data || {};
    if (d.estado === 'OPERATIVA') return 'La puesta en marcha terminó: el comercio quedó operando';
    if (d.estado === 'FALLO') return `La puesta en marcha quedó con ${d.faltan || 'algunos'} pendiente${d.faltan === 1 ? '' : 's'}`;
    return 'La puesta en marcha está en curso';
  }

  /** Cada paso de la puesta en marcha, como está hoy, con su marca. */
  get pasosPuesta(): Array<{ nombre: string; marca: string; tono: string }> {
    const g = (this.sol?.puesta_en_marcha as any)?.grupos || [];
    return g.flatMap((x: any) => x.pasos || []).filter((p: any) => p.estado !== 'NO_APLICA').map((p: any) => ({
      nombre: p.nombre || p.clave,
      marca: p.estado === 'VERIFICADO' || p.estado === 'HECHO' ? '✓' : p.estado === 'FALLO' ? '✗' : '',
      tono: p.estado === 'VERIFICADO' || p.estado === 'HECHO' ? 'ok' : p.estado === 'FALLO' ? 'mal' : 'pend',
    }));
  }

  claseVerbo(tipo: TipoEvento): string {
    if (tipo === 'APROBADA') return 'kb-texto-exito';
    if (tipo === 'DEVUELTA') return 'kb-texto-alerta';
    if (tipo === 'RECHAZADA') return 'kb-texto-peligro';
    return '';
  }

  verboDecision(tipo: TipoEvento): string {
    if (tipo === 'APROBADA') return 'aprobó';
    if (tipo === 'DEVUELTA') return 'devolvió';
    if (tipo === 'RECHAZADA') return 'rechazó';
    return '';
  }

  /** «y la pasó a X» / «y terminó el workflow», leído de `data.destino` (ver el contrato del tipo). */
  destinoTexto(ev: EventoSolicitud): string {
    const destino = ev.data?.destino;
    if (!destino) return '';
    if (destino.rol_nombre) return `y la pasó a ${destino.rol_nombre}`;
    if (destino.tipo === 'FIN') return `y terminó el workflow`;
    return '';
  }

  nombreMotivo(codigo: string | null): string {
    if (!codigo) return '';
    return this.motivosList.find((m) => m.codigo === codigo)?.nombre || codigo;
  }

  camposTexto(ev: EventoSolicitud): string {
    const campos: string[] = Array.isArray(ev.data?.campos) ? ev.data.campos : [];
    return campos.map((c) => ETIQUETAS_CAMPO[c] || c).join(', ');
  }
}
